import React, { useState, useEffect } from 'react';
import { 
  VENDOR_TYPES, 
  getVendorType, 
  getVendorTypeDisplayName,
  isVendor,
  getVendorPermissions,
  PERMISSIONS
} from '../utils/rbac';
import { 
  BuildingOfficeIcon,
  UserGroupIcon,
  CogIcon,
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const VendorManagement = ({ user, onUpdateUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [filterVendorType, setFilterVendorType] = useState('all');

  useEffect(() => {
    fetchVendorUsers();
  }, []);

  const fetchVendorUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/vendors`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.vendors || []);
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserVendorType = async (userId, vendorType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/users/${userId}/vendor-type`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vendorType })
      });

      if (response.ok) {
        // Update local state
        setUsers(users.map(u => 
          u.id === userId ? { ...u, vendorType } : u
        ));
        setShowVendorModal(false);
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Failed to update vendor type:', error);
    }
  };

  const filteredUsers = users.filter(user => {
    if (filterVendorType === 'all') return true;
    return getVendorType(user.rules) === filterVendorType || user.vendorType === filterVendorType;
  });

  const getVendorStats = () => {
    const stats = {};
    Object.values(VENDOR_TYPES).forEach(type => {
      stats[type] = users.filter(u => 
        getVendorType(u.rules) === type || u.vendorType === type
      ).length;
    });
    return stats;
  };

  const vendorStats = getVendorStats();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Vendor Management
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Manage vendor types and their specific permissions
          </p>
        </div>
        <BuildingOfficeIcon className="w-8 h-8 text-blue-600" />
      </div>

      {/* Vendor Type Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Object.entries(VENDOR_TYPES).map(([key, type]) => (
          <div key={type} className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {vendorStats[type] || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {key}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <select
          value={filterVendorType}
          onChange={(e) => setFilterVendorType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="all">All Vendor Types</option>
          {Object.entries(VENDOR_TYPES).map(([key, type]) => (
            <option key={type} value={type}>
              {key}
            </option>
          ))}
        </select>
      </div>

      {/* Vendor Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((vendorUser) => {
                const vendorType = getVendorType(vendorUser.rules) || vendorUser.vendorType;
                const permissions = getVendorPermissions(vendorUser.rules);
                
                return (
                  <tr key={vendorUser.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <BuildingOfficeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {vendorUser.name || 'No Name'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {vendorUser.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vendorType ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                          {getVendorTypeDisplayName(vendorType)}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          Not Set
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {permissions.slice(0, 3).map((permission) => (
                          <span
                            key={permission}
                            className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded dark:bg-blue-900 dark:text-blue-300"
                          >
                            {permission.replace(/_/g, ' ').toLowerCase()}
                          </span>
                        ))}
                        {permissions.length > 3 && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded dark:bg-gray-700 dark:text-gray-400">
                            +{permissions.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedUser(vendorUser);
                          setShowVendorModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit Vendor Type"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {filterVendorType !== 'all' ? 'No vendors of this type' : 'No vendors found'}
            </p>
          </div>
        )}
      </div>

      {/* Vendor Type Assignment Modal */}
      {showVendorModal && selectedUser && (
        <VendorTypeModal
          user={selectedUser}
          onSave={(vendorType) => updateUserVendorType(selectedUser.id, vendorType)}
          onClose={() => {
            setShowVendorModal(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
};

// Vendor Type Assignment Modal
const VendorTypeModal = ({ user, onSave, onClose }) => {
  const [selectedVendorType, setSelectedVendorType] = useState(
    getVendorType(user.rules) || user.vendorType || VENDOR_TYPES.GENERAL
  );
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    await onSave(selectedVendorType);
    setLoading(false);
  };

  const getPermissionsForType = (vendorType) => {
    // This would normally come from VENDOR_TYPE_PERMISSIONS
    // For now, return a subset based on type
    const basePermissions = [PERMISSIONS.VIEW_CALENDAR, PERMISSIONS.VIEW_OWN_FORMS];
    
    switch (vendorType) {
      case VENDOR_TYPES.GAC:
        return [...basePermissions, PERMISSIONS.VIEW_FORMS, PERMISSIONS.CREATE_FORMS, PERMISSIONS.VIEW_PENDING_APPROVALS];
      case VENDOR_TYPES.SGS:
      case VENDOR_TYPES.SAFE:
        return [...basePermissions, PERMISSIONS.VIEW_FORMS, PERMISSIONS.CREATE_FORMS, PERMISSIONS.APPROVE_FORMS];
      default:
        return basePermissions;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Set Vendor Type
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Vendor: <strong>{user.name || user.email}</strong>
          </p>
        </div>

        <div className="space-y-4 mb-6">
          {Object.entries(VENDOR_TYPES).map(([key, type]) => (
            <label key={type} className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="vendorType"
                value={type}
                checked={selectedVendorType === type}
                onChange={(e) => setSelectedVendorType(e.target.value)}
                className="mt-1 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {getVendorTypeDisplayName(type)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Permissions: {getPermissionsForType(type).length} granted
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorManagement;
